/**
 * Refining a question before it is asked.
 *
 * The reader types "teach me PDE" and means something much more specific that
 * they have not said — usually because saying it requires knowing the shape of
 * the subject, which is what they came here to find out. Refinement makes that
 * implicit request explicit *in their own text box*, where they can read it,
 * edit it, or throw it away before a single token is spent.
 *
 * The important property is that it commits to nothing. It is a proposal in the
 * medium the reader already controls, not a question they must answer.
 */

import { z } from "zod";

/** Below this there is no question yet — "hi" is not underspecified. */
export const MIN_REFINABLE_LENGTH = 6;

/**
 * Past this, the reader has already been more specific than a rewrite could
 * make them, and re-reading three paragraphs to find what changed costs more
 * than it saves.
 */
export const MAX_REFINABLE_LENGTH = 4_000;

export const refineRequestSchema = z.object({
  content: z
    .string()
    .trim()
    .min(MIN_REFINABLE_LENGTH, "There isn't a question here yet.")
    .max(MAX_REFINABLE_LENGTH, "That's already detailed enough to send."),
});

export type RefineRequest = z.infer<typeof refineRequestSchema>;

/** What the model is asked for. Whether it *changed* anything is not its call. */
export const refinementDraftSchema = z.object({
  refined: z.string().min(1).max(MAX_REFINABLE_LENGTH),
  /**
   * One line naming what was made explicit — "assumed you know vector
   * calculus", "narrowed to first-order equations". This is what lets the
   * reader spot an assumption they disagree with without diffing two
   * paragraphs by eye.
   */
  note: z.string().max(200).default(""),
});

export interface Refinement {
  readonly refined: string;
  /**
   * Derived by comparing the text, never self-reported. A model asked whether
   * it changed something will say yes, because it was asked to.
   */
  readonly changed: boolean;
  readonly note: string;
}

/** Whether the button should be offered at all for the current input. */
export function isRefinable(content: string): boolean {
  const trimmed = content.trim();
  return (
    trimmed.length >= MIN_REFINABLE_LENGTH &&
    trimmed.length <= MAX_REFINABLE_LENGTH
  );
}

/**
 * Same question or not, ignoring whitespace the reader cannot see.
 *
 * A refinement that only re-wraps lines has changed nothing worth telling
 * anyone about, and offering to revert it would be noise.
 */
export function sameQuestion(a: string, b: string): boolean {
  const normalize = (text: string) => text.trim().replace(/\s+/g, " ");
  return normalize(a) === normalize(b);
}
